// SeasonPreflightDialog — 3-step coach-voiced wizard.
// Collects goal, timeline, and training frequency before plan generation.
// Matches the dark HUD glass aesthetic of SeasonView.

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/overlay/dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, Target, TrendingUp, Dumbbell } from 'lucide-react';
import { format, addMonths } from 'date-fns';

const TEAL = '0, 255, 200';
const GREY_TEXT = '#A0A0A0';
const WHITE_TEXT = '#E8E8E8';

type GoalType = 'race' | 'base_building' | 'fitness';
type RaceDistance = '5K' | '10K' | 'Half Marathon' | 'Marathon' | 'Ultra';
type DaysPerWeek = 3 | 4 | 5 | 6;

interface PreflightState {
  goalType: GoalType | null;
  raceDistance: RaceDistance | null;
  raceDate: string;
  seasonEnd: string;
  daysPerWeek: DaysPerWeek | null;
}

const DEFAULT_STATE: PreflightState = {
  goalType: null,
  raceDistance: null,
  raceDate: '',
  seasonEnd: '',
  daysPerWeek: null,
};

const GOAL_OPTIONS: Array<{
  value: GoalType;
  label: string;
  subtitle: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'race',
    label: 'Race',
    subtitle: 'Goal race with full taper block',
    icon: <Target className="h-4 w-4" />,
  },
  {
    value: 'base_building',
    label: 'Base Building',
    subtitle: 'Aerobic foundation & volume',
    icon: <TrendingUp className="h-4 w-4" />,
  },
  {
    value: 'fitness',
    label: 'General Fitness',
    subtitle: 'Health & consistent training',
    icon: <Dumbbell className="h-4 w-4" />,
  },
];

const RACE_DISTANCES: RaceDistance[] = ['5K', '10K', 'Half Marathon', 'Marathon', 'Ultra'];
const DAY_OPTIONS: DaysPerWeek[] = [3, 4, 5, 6];

const DAY_DESCRIPTIONS: Record<DaysPerWeek, string> = {
  3: 'Long · Tempo · Easy',
  4: 'Balanced — ideal for most',
  5: 'Optimal competitive training',
  6: 'High volume · advanced',
};

function buildCoachMessage(s: PreflightState): string {
  const days = s.daysPerWeek ?? 5;
  if (s.goalType === 'race' && s.raceDistance && s.raceDate) {
    return `Build my season plan for a ${s.raceDistance} on ${s.raceDate}, training ${days} days per week`;
  }
  const end = s.seasonEnd || format(addMonths(new Date(), 3), 'yyyy-MM-dd');
  if (s.goalType === 'base_building') {
    return `Build a base building season plan until ${end}, training ${days} days per week`;
  }
  return `Build a fitness training plan until ${end}, training ${days} days per week`;
}

interface SeasonPreflightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (message: string) => void;
}

export function SeasonPreflightDialog({
  open,
  onOpenChange,
  onGenerate,
}: SeasonPreflightDialogProps) {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<PreflightState>(DEFAULT_STATE);

  const STEP_LABELS = ['Goal', 'Timeline', 'Frequency'];
  const STEP_QUESTIONS = [
    'What are we training for?',
    state.goalType === 'race'
      ? 'Tell me about your race.'
      : 'How long is your season?',
    'How many days can you train per week?',
  ];

  const canProceed = (): boolean => {
    if (step === 0) return state.goalType !== null;
    if (step === 1) {
      if (state.goalType === 'race') {
        return !!state.raceDistance && !!state.raceDate;
      }
      return !!state.seasonEnd;
    }
    if (step === 2) return state.daysPerWeek !== null;
    return false;
  };

  const handleNext = () => {
    if (step < STEP_LABELS.length - 1) {
      setStep(s => s + 1);
    } else {
      onGenerate(buildCoachMessage(state));
      // Reset for next use
      setStep(0);
      setState(DEFAULT_STATE);
      onOpenChange(false);
    }
  };

  const handleBack = () => setStep(s => Math.max(0, s - 1));

  const handleClose = () => {
    setStep(0);
    setState(DEFAULT_STATE);
    onOpenChange(false);
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    background: active ? `rgba(${TEAL}, 0.1)` : 'rgba(255,255,255,0.03)',
    border: `1px solid ${active ? `rgba(${TEAL}, 0.35)` : 'rgba(255,255,255,0.08)'}`,
    color: active ? `rgba(${TEAL}, 0.95)` : WHITE_TEXT,
    transition: 'all 0.15s ease',
  });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[420px] p-0 overflow-hidden gap-0"
        style={{
          background: 'rgba(8, 12, 20, 0.98)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.09)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.04), 0 24px 64px rgba(0,0,0,0.75)',
        }}
      >
        {/* Step tabs */}
        <div className="flex border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {STEP_LABELS.map((label, i) => (
            <div
              key={i}
              className="flex-1 py-3 text-center"
              style={{
                borderBottom:
                  i === step
                    ? `2px solid rgba(${TEAL}, 0.75)`
                    : '2px solid transparent',
              }}
            >
              <span
                className="font-mono text-[9px] uppercase tracking-widest"
                style={{
                  color:
                    i === step
                      ? `rgba(${TEAL}, 0.9)`
                      : i < step
                        ? `rgba(${TEAL}, 0.35)`
                        : GREY_TEXT,
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Coach voice */}
          <div>
            <p
              className="font-mono text-[9px] uppercase tracking-widest mb-1.5"
              style={{ color: `rgba(${TEAL}, 0.55)` }}
            >
              Coach
            </p>
            <p className="text-base font-medium" style={{ color: WHITE_TEXT }}>
              {STEP_QUESTIONS[step]}
            </p>
          </div>

          {/* Step 0 — Goal type */}
          {step === 0 && (
            <div className="space-y-2">
              {GOAL_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setState(s => ({ ...s, goalType: opt.value }))}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left"
                  style={chipStyle(state.goalType === opt.value)}
                >
                  <span
                    style={{
                      color:
                        state.goalType === opt.value
                          ? `rgba(${TEAL}, 0.8)`
                          : GREY_TEXT,
                    }}
                  >
                    {opt.icon}
                  </span>
                  <div>
                    <p
                      className="font-medium text-sm"
                      style={{
                        color:
                          state.goalType === opt.value
                            ? `rgba(${TEAL}, 0.95)`
                            : WHITE_TEXT,
                      }}
                    >
                      {opt.label}
                    </p>
                    <p
                      className="font-mono text-[10px] mt-0.5"
                      style={{ color: GREY_TEXT }}
                    >
                      {opt.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 1 — Race details */}
          {step === 1 && state.goalType === 'race' && (
            <div className="space-y-4">
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-wider mb-2"
                  style={{ color: GREY_TEXT }}
                >
                  Distance
                </p>
                <div className="flex flex-wrap gap-2">
                  {RACE_DISTANCES.map(d => (
                    <button
                      key={d}
                      onClick={() => setState(s => ({ ...s, raceDistance: d }))}
                      className="px-3 py-1.5 rounded-md font-mono text-xs"
                      style={chipStyle(state.raceDistance === d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-wider mb-2"
                  style={{ color: GREY_TEXT }}
                >
                  Race Date
                </p>
                <input
                  type="date"
                  value={state.raceDate}
                  onChange={e => setState(s => ({ ...s, raceDate: e.target.value }))}
                  min={format(addMonths(new Date(), 1), 'yyyy-MM-dd')}
                  className="w-full px-3 py-2 rounded-lg font-mono text-sm focus:outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: WHITE_TEXT,
                    colorScheme: 'dark',
                  }}
                />
              </div>
            </div>
          )}

          {/* Step 1 — Season end for non-race */}
          {step === 1 && state.goalType !== 'race' && (
            <div className="space-y-3">
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-wider mb-2"
                  style={{ color: GREY_TEXT }}
                >
                  Season End Date
                </p>
                <input
                  type="date"
                  value={state.seasonEnd}
                  onChange={e => setState(s => ({ ...s, seasonEnd: e.target.value }))}
                  min={format(addMonths(new Date(), 1), 'yyyy-MM-dd')}
                  className="w-full px-3 py-2 rounded-lg font-mono text-sm focus:outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: WHITE_TEXT,
                    colorScheme: 'dark',
                  }}
                />
              </div>
              <p className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                Minimum 4 weeks recommended for a structured plan.
              </p>
            </div>
          )}

          {/* Step 2 — Days per week */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex gap-2">
                {DAY_OPTIONS.map(d => (
                  <button
                    key={d}
                    onClick={() => setState(s => ({ ...s, daysPerWeek: d }))}
                    className="flex-1 py-3 rounded-lg font-mono text-sm font-medium"
                    style={chipStyle(state.daysPerWeek === d)}
                  >
                    {d}
                  </button>
                ))}
              </div>
              {state.daysPerWeek && (
                <p className="font-mono text-[10px]" style={{ color: GREY_TEXT }}>
                  {DAY_DESCRIPTIONS[state.daysPerWeek]}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={step === 0 ? handleClose : handleBack}
            className="font-mono text-xs text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
          >
            {step === 0 ? (
              'Cancel'
            ) : (
              <span className="flex items-center">
                <ChevronLeft className="h-3 w-3 mr-1" />
                Back
              </span>
            )}
          </Button>

          <Button
            size="sm"
            onClick={handleNext}
            disabled={!canProceed()}
            className="font-mono text-xs"
            style={
              canProceed()
                ? {
                    background: `rgba(${TEAL}, 0.12)`,
                    border: `1px solid rgba(${TEAL}, 0.3)`,
                    color: `rgba(${TEAL}, 0.95)`,
                  }
                : {
                    opacity: 0.4,
                  }
            }
          >
            {step === STEP_LABELS.length - 1 ? (
              'Generate Plan'
            ) : (
              <span className="flex items-center">
                Next
                <ChevronRight className="h-3 w-3 ml-1" />
              </span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
